import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { 
  getComplianceDashboard, 
  getComplianceMonthlySummary,
  getComplianceStates,
  getPanIndiaSummary,
  getStateLaws,
  downloadPfEcrReport,
  downloadEsiReport,
  downloadPtReport,
  downloadTdsReport,
  downloadLwfReport,
  downloadForm16,
  downloadForm24Q,
  getEmployees
} from '../api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '../components/ui/dialog';
import { 
  FileText, Download, Calendar, AlertCircle, CheckCircle, Clock, 
  Building, Users, IndianRupee, TrendingUp, Shield, FileSpreadsheet,
  MapPin, Globe, Scale, ChevronRight, Receipt, FileCheck
} from 'lucide-react';
import { toast } from 'sonner';

const months = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const currentYear = new Date().getFullYear();
const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

const StatutoryCompliance = ({ user, setUser }) => {
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [monthlySummary, setMonthlySummary] = useState(null);
  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState('PAN India');
  const [stateLaws, setStateLaws] = useState(null);
  const [panIndiaSummary, setPanIndiaSummary] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(months[new Date().getMonth()]);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedQuarter, setSelectedQuarter] = useState(Math.ceil((new Date().getMonth() + 1) / 3));
  const [downloading, setDownloading] = useState({});
  
  // Form 16 & 24Q states
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [selectedFinancialYear, setSelectedFinancialYear] = useState(`${currentYear}-${(currentYear + 1).toString().slice(-2)}`);
  const [form16DialogOpen, setForm16DialogOpen] = useState(false);
  const [form24QDialogOpen, setForm24QDialogOpen] = useState(false);
  const [form24QQuarter, setForm24QQuarter] = useState(Math.ceil((new Date().getMonth() + 1) / 3));
  
  // Financial years for dropdown
  const financialYears = Array.from({ length: 5 }, (_, i) => {
    const year = currentYear - i;
    return `${year}-${(year + 1).toString().slice(-2)}`;
  });

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (selectedMonth && selectedYear) {
      fetchMonthlySummary();
      fetchPanIndiaSummary();
    }
  }, [selectedMonth, selectedYear]);

  useEffect(() => {
    if (selectedState && selectedState !== 'PAN India') {
      fetchStateLaws(selectedState);
    } else {
      setStateLaws(null);
    }
  }, [selectedState]);

  const fetchInitialData = async () => {
    try {
      const [dashboardRes, statesRes, employeesRes] = await Promise.all([
        getComplianceDashboard(),
        getComplianceStates(),
        getEmployees()
      ]);
      setDashboard(dashboardRes.data);
      setStates(statesRes.data);
      setEmployees(employeesRes.data || []);
    } catch (error) {
      toast.error('Failed to fetch compliance data');
    } finally {
      setLoading(false);
    }
  };

  const fetchMonthlySummary = async () => {
    try {
      const response = await getComplianceMonthlySummary(selectedMonth, selectedYear);
      setMonthlySummary(response.data);
    } catch (error) {
      console.error('Failed to fetch monthly summary');
    }
  };

  const fetchPanIndiaSummary = async () => {
    try {
      const response = await getPanIndiaSummary(selectedMonth, selectedYear);
      setPanIndiaSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch PAN India summary');
    }
  };

  const fetchStateLaws = async (state) => {
    try {
      const response = await getStateLaws(state);
      setStateLaws(response.data);
    } catch (error) {
      console.error('Failed to fetch state laws');
    }
  };

  const handleDownloadReport = async (reportType, format) => {
    const key = `${reportType}_${format}`;
    setDownloading(prev => ({ ...prev, [key]: true }));
    
    try {
      let response;
      let filename;
      
      switch (reportType) {
        case 'pf':
          response = await downloadPfEcrReport(selectedMonth, selectedYear, format);
          filename = `PF_ECR_${selectedMonth}_${selectedYear}.${format === 'excel' ? 'xlsx' : format}`;
          break;
        case 'esi':
          response = await downloadEsiReport(selectedMonth, selectedYear, format);
          filename = `ESI_Report_${selectedMonth}_${selectedYear}.${format === 'excel' ? 'xlsx' : format}`;
          break;
        case 'pt':
          response = await downloadPtReport(selectedMonth, selectedYear, format);
          filename = `PT_Report_${selectedMonth}_${selectedYear}.${format === 'excel' ? 'xlsx' : format}`;
          break;
        case 'tds':
          response = await downloadTdsReport(selectedQuarter, selectedYear, format);
          filename = `TDS_Q${selectedQuarter}_${selectedYear}.${format === 'excel' ? 'xlsx' : format}`;
          break;
        case 'lwf':
          response = await downloadLwfReport(selectedMonth, selectedYear, format);
          filename = `LWF_Report_${selectedMonth}_${selectedYear}.${format === 'excel' ? 'xlsx' : format}`;
          break;
        default:
          throw new Error('Unknown report type');
      }
      
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(`${reportType.toUpperCase()} report downloaded`);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to download ${reportType.toUpperCase()} report`);
    } finally {
      setDownloading(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleDownloadForm16 = async () => {
    if (!selectedEmployee) {
      toast.error('Please select an employee');
      return;
    }
    
    setDownloading(prev => ({ ...prev, form16: true }));
    try {
      const response = await downloadForm16(selectedEmployee, selectedFinancialYear);
      const employee = employees.find(e => e.id === selectedEmployee);
      const empName = (employee?.name || employee?.employee_id || 'employee').replace(/\s+/g, '_');
      const filename = `Form16_${empName}_${selectedFinancialYear}.pdf`;
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Form 16 downloaded successfully');
      setForm16DialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to download Form 16');
    } finally {
      setDownloading(prev => ({ ...prev, form16: false }));
    }
  };

  const handleDownloadForm24Q = async () => {
    setDownloading(prev => ({ ...prev, form24q: true }));
    try {
      const response = await downloadForm24Q(form24QQuarter, selectedFinancialYear);
      const filename = `Form24Q_Q${form24QQuarter}_${selectedFinancialYear}.pdf`;
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Form 24Q downloaded successfully');
      setForm24QDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to download Form 24Q');
    } finally {
      setDownloading(prev => ({ ...prev, form24q: false }));
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'completed':
        return <span className="flex items-center gap-1 text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full text-xs"><CheckCircle size={12} /> Completed</span>;
      case 'due':
        return <span className="flex items-center gap-1 text-amber-600 bg-amber-50 px-2 py-1 rounded-full text-xs"><Clock size={12} /> Due</span>;
      case 'overdue':
        return <span className="flex items-center gap-1 text-rose-600 bg-rose-50 px-2 py-1 rounded-full text-xs"><AlertCircle size={12} /> Overdue</span>;
      default:
        return <span className="text-slate-500 text-xs">-</span>;
    }
  };

  if (loading) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6">
        {/* Header with Location Selector */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Statutory Compliance</h1>
            <p className="text-slate-600 mt-1">Manage PF, ESI, LWF, TDS and other statutory requirements</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 bg-white border rounded-lg px-3 py-2">
              <MapPin size={18} className="text-indigo-600" />
              <Select value={selectedState} onValueChange={setSelectedState}>
                <SelectTrigger className="w-[180px] border-0 p-0 h-auto focus:ring-0">
                  <SelectValue placeholder="Select Location" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PAN India">
                    <div className="flex items-center gap-2">
                      <Globe size={14} />
                      PAN India
                    </div>
                  </SelectItem>
                  {states.filter(s => s.employee_count > 0).map(state => (
                    <SelectItem key={state.name} value={state.name}>
                      <div className="flex items-center justify-between w-full">
                        <span>{state.name}</span>
                        <span className="text-xs text-slate-400 ml-2">({state.employee_count})</span>
                      </div>
                    </SelectItem>
                  ))}
                  <div className="border-t my-1"></div>
                  <div className="px-2 py-1 text-xs text-slate-500">All States</div>
                  {states.filter(s => s.employee_count === 0).map(state => (
                    <SelectItem key={state.name} value={state.name}>
                      {state.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm">Total Employees</p>
                  <p className="text-3xl font-bold mt-1">{dashboard?.summary?.total_employees || 0}</p>
                </div>
                <Users className="h-12 w-12 text-blue-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-emerald-100 text-sm">PF Eligible</p>
                  <p className="text-3xl font-bold mt-1">{dashboard?.summary?.pf_eligible_employees || 0}</p>
                </div>
                <Shield className="h-12 w-12 text-emerald-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-amber-100 text-sm">ESI Eligible</p>
                  <p className="text-3xl font-bold mt-1">{dashboard?.summary?.esi_eligible_employees || 0}</p>
                </div>
                <TrendingUp className="h-12 w-12 text-amber-200" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-sm">States Active</p>
                  <p className="text-3xl font-bold mt-1">{states.filter(s => s.employee_count > 0).length}</p>
                </div>
                <MapPin className="h-12 w-12 text-purple-200" />
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="pan-india" className="space-y-4">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="pan-india" className="flex items-center gap-1">
              <Globe size={14} />
              PAN India
            </TabsTrigger>
            <TabsTrigger value="state-laws" className="flex items-center gap-1">
              <Scale size={14} />
              State Laws
            </TabsTrigger>
            <TabsTrigger value="summary">Monthly Summary</TabsTrigger>
            <TabsTrigger value="reports">Generate Reports</TabsTrigger>
            <TabsTrigger value="tds-forms" className="flex items-center gap-1">
              <Receipt size={14} />
              TDS Forms
            </TabsTrigger>
            <TabsTrigger value="rates">Rates</TabsTrigger>
          </TabsList>

          {/* PAN India Tab */}
          <TabsContent value="pan-india">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="text-indigo-600" size={20} />
                  PAN India Compliance Summary - {selectedMonth} {selectedYear}
                </CardTitle>
                <CardDescription>Consolidated statutory compliance across all states</CardDescription>
              </CardHeader>
              <CardContent>
                {/* Period Selector */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="space-y-2">
                    <Label>Month</Label>
                    <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {months.map(month => (
                          <SelectItem key={month} value={month}>{month}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Year</Label>
                    <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {years.map(year => (
                          <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {panIndiaSummary ? (
                  <div className="space-y-6">
                    {/* PAN India Totals */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <Card className="bg-blue-50 border-blue-200">
                        <CardContent className="p-4 text-center">
                          <p className="text-xs text-blue-600 font-medium">Est. PF (EE+ER)</p>
                          <p className="text-xl font-bold text-blue-800">₹{panIndiaSummary.pan_india_totals?.estimated_pf_total?.toLocaleString()}</p>
                        </CardContent>
                      </Card>
                      <Card className="bg-emerald-50 border-emerald-200">
                        <CardContent className="p-4 text-center">
                          <p className="text-xs text-emerald-600 font-medium">Est. ESI (EE+ER)</p>
                          <p className="text-xl font-bold text-emerald-800">₹{panIndiaSummary.pan_india_totals?.estimated_esi_total?.toLocaleString()}</p>
                        </CardContent>
                      </Card>
                      <Card className="bg-amber-50 border-amber-200">
                        <CardContent className="p-4 text-center">
                          <p className="text-xs text-amber-600 font-medium">Est. Professional Tax</p>
                          <p className="text-xl font-bold text-amber-800">₹{panIndiaSummary.pan_india_totals?.estimated_pt_total?.toLocaleString()}</p>
                        </CardContent>
                      </Card>
                      <Card className="bg-indigo-50 border-indigo-200">
                        <CardContent className="p-4 text-center">
                          <p className="text-xs text-indigo-600 font-medium">Est. Total Statutory</p>
                          <p className="text-xl font-bold text-indigo-800">₹{panIndiaSummary.pan_india_totals?.estimated_total_statutory?.toLocaleString()}</p>
                        </CardContent>
                      </Card>
                    </div>

                    {/* State-wise Breakdown */}
                    <div>
                      <h3 className="text-lg font-semibold text-slate-800 mb-4">State-wise Breakdown</h3>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-slate-50">
                            <tr>
                              <th className="text-left p-3 font-medium">State</th>
                              <th className="text-center p-3 font-medium">Employees</th>
                              <th className="text-center p-3 font-medium">PF</th>
                              <th className="text-center p-3 font-medium">ESI</th>
                              <th className="text-center p-3 font-medium">PT</th>
                              <th className="text-center p-3 font-medium">LWF</th>
                              <th className="text-right p-3 font-medium">Est. Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {panIndiaSummary.state_wise_breakdown?.filter(s => s.total_employees > 0).map((stateData, idx) => (
                              <tr key={idx} className="border-t hover:bg-slate-50">
                                <td className="p-3">
                                  <div className="flex items-center gap-2">
                                    <MapPin size={14} className="text-slate-400" />
                                    <span className="font-medium">{stateData.state}</span>
                                  </div>
                                </td>
                                <td className="p-3 text-center">{stateData.total_employees}</td>
                                <td className="p-3 text-center">
                                  <span className="text-blue-600">₹{(stateData.estimated_pf_employee + stateData.estimated_pf_employer).toLocaleString()}</span>
                                </td>
                                <td className="p-3 text-center">
                                  <span className="text-emerald-600">₹{(stateData.estimated_esi_employee + stateData.estimated_esi_employer).toLocaleString()}</span>
                                </td>
                                <td className="p-3 text-center">
                                  {stateData.pt_applicable ? (
                                    <span className="text-amber-600">₹{stateData.estimated_pt.toLocaleString()}</span>
                                  ) : (
                                    <span className="text-slate-400">N/A</span>
                                  )}
                                </td>
                                <td className="p-3 text-center">
                                  {stateData.lwf_applicable ? (
                                    <span className="text-orange-600">₹{(stateData.estimated_lwf_employee + stateData.estimated_lwf_employer).toLocaleString()}</span>
                                  ) : (
                                    <span className="text-slate-400">N/A</span>
                                  )}
                                </td>
                                <td className="p-3 text-right font-semibold">
                                  ₹{(
                                    stateData.estimated_pf_employee + stateData.estimated_pf_employer +
                                    stateData.estimated_esi_employee + stateData.estimated_esi_employer +
                                    stateData.estimated_pt +
                                    stateData.estimated_lwf_employee + stateData.estimated_lwf_employer
                                  ).toLocaleString()}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot className="bg-indigo-50 font-bold">
                            <tr>
                              <td className="p-3">PAN India Total</td>
                              <td className="p-3 text-center">{panIndiaSummary.pan_india_totals?.total_employees}</td>
                              <td className="p-3 text-center text-blue-700">₹{panIndiaSummary.pan_india_totals?.estimated_pf_total?.toLocaleString()}</td>
                              <td className="p-3 text-center text-emerald-700">₹{panIndiaSummary.pan_india_totals?.estimated_esi_total?.toLocaleString()}</td>
                              <td className="p-3 text-center text-amber-700">₹{panIndiaSummary.pan_india_totals?.estimated_pt_total?.toLocaleString()}</td>
                              <td className="p-3 text-center text-orange-700">₹{panIndiaSummary.pan_india_totals?.estimated_lwf_total?.toLocaleString()}</td>
                              <td className="p-3 text-right text-indigo-700">₹{panIndiaSummary.pan_india_totals?.estimated_total_statutory?.toLocaleString()}</td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  </div>
                ) : (
                  <p className="text-center text-slate-500 py-8">Loading PAN India summary...</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* State Laws Tab */}
          <TabsContent value="state-laws">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* State Selector */}
              <Card className="lg:col-span-1">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="text-indigo-600" size={20} />
                    Select State
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {states.map(state => (
                      <button
                        key={state.name}
                        onClick={() => setSelectedState(state.name)}
                        className={`w-full flex items-center justify-between p-3 rounded-lg text-left transition-colors ${
                          selectedState === state.name 
                            ? 'bg-indigo-100 text-indigo-800 border border-indigo-200' 
                            : 'bg-slate-50 hover:bg-slate-100 text-slate-700'
                        }`}
                      >
                        <div>
                          <p className="font-medium">{state.name}</p>
                          <p className="text-xs text-slate-500">
                            {state.employee_count > 0 ? `${state.employee_count} employees` : 'No employees'}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {state.has_pt && <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded">PT</span>}
                          {state.has_lwf && <span className="text-[10px] bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded">LWF</span>}
                          <ChevronRight size={16} className="text-slate-400" />
                        </div>
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* State Laws Display */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Scale className="text-indigo-600" size={20} />
                    Applicable Laws - {selectedState || 'Select a State'}
                  </CardTitle>
                  <CardDescription>Statutory requirements as per Indian laws</CardDescription>
                </CardHeader>
                <CardContent>
                  {stateLaws ? (
                    <div className="space-y-4">
                      {/* PF */}
                      {stateLaws.pf && (
                        <Card className="bg-blue-50/50 border-blue-200">
                          <CardHeader className="pb-2">
                            <CardTitle className="text-base text-blue-800 flex items-center gap-2">
                              <Shield size={16} />
                              {stateLaws.pf.name || 'Provident Fund (PF)'}
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="text-sm space-y-2">
                            <p className="text-slate-600">{stateLaws.pf.description || 'Employee Provident Fund scheme'}</p>
                            <div className="grid grid-cols-2 gap-2">
                              <div className="bg-white p-2 rounded">
                                <p className="text-xs text-slate-500">Employee</p>
                                <p className="font-medium">{stateLaws.pf.employee_rate || '12%'}</p>
                              </div>
                              <div className="bg-white p-2 rounded">
                                <p className="text-xs text-slate-500">Employer</p>
                                <p className="font-medium">{stateLaws.pf.employer_rate || '12%'}</p>
                              </div>
                            </div>
                            <p className="text-xs text-slate-500">Wage Ceiling: {stateLaws.pf.wage_ceiling || '₹15,000/month'} | Due: {stateLaws.pf.due_date || '15th of following month'}</p>
                          </CardContent>
                        </Card>
                      )}

                      {/* ESI */}
                      {stateLaws.esi && (
                        <Card className="bg-emerald-50/50 border-emerald-200">
                          <CardHeader className="pb-2">
                            <CardTitle className="text-base text-emerald-800 flex items-center gap-2">
                              <Shield size={16} />
                              {stateLaws.esi.name || 'Employee State Insurance (ESI)'}
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="text-sm space-y-2">
                            <p className="text-slate-600">{stateLaws.esi.description || 'Medical insurance scheme'}</p>
                            <div className="grid grid-cols-2 gap-2">
                              <div className="bg-white p-2 rounded">
                                <p className="text-xs text-slate-500">Employee</p>
                                <p className="font-medium">{stateLaws.esi.employee_rate || '0.75%'}</p>
                              </div>
                              <div className="bg-white p-2 rounded">
                                <p className="text-xs text-slate-500">Employer</p>
                                <p className="font-medium">{stateLaws.esi.employer_rate || '3.25%'}</p>
                              </div>
                            </div>
                            <p className="text-xs text-slate-500">Eligibility: {stateLaws.esi.wage_ceiling || '₹21,000'} | Due: {stateLaws.esi.due_date || '15th'}</p>
                          </CardContent>
                        </Card>
                      )}

                      {/* Professional Tax */}
                      {stateLaws.professional_tax && (
                        <Card className={`${stateLaws.professional_tax.applicable ? 'bg-amber-50/50 border-amber-200' : 'bg-slate-50 border-slate-200'}`}>
                          <CardHeader className="pb-2">
                            <CardTitle className={`text-base flex items-center gap-2 ${stateLaws.professional_tax.applicable ? 'text-amber-800' : 'text-slate-500'}`}>
                              <Scale size={16} />
                              {stateLaws.professional_tax.name || 'Professional Tax'}
                              {!stateLaws.professional_tax.applicable && <span className="text-xs bg-slate-200 px-2 py-0.5 rounded">Not Applicable</span>}
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="text-sm">
                            {stateLaws.professional_tax.applicable ? (
                              <div className="space-y-2">
                                <p className="text-slate-600">{stateLaws.professional_tax.description || 'State tax on employment'}</p>
                                <div className="bg-white p-3 rounded">
                                  <p className="text-xs text-slate-500 mb-2">Monthly Slabs:</p>
                                  <ul className="space-y-1">
                                    {(stateLaws.professional_tax.slabs || []).map((slab, i) => (
                                      <li key={i} className="text-sm">₹{slab.min?.toLocaleString()} - ₹{slab.max?.toLocaleString()}: ₹{slab.tax}</li>
                                    ))}
                                  </ul>
                                </div>
                                <p className="text-xs text-slate-500">Max: {stateLaws.professional_tax.max_annual || '₹2,500/year'} | Due: {stateLaws.professional_tax.due_date || 'End of month'}</p>
                              </div>
                            ) : (
                              <p className="text-slate-500">Professional Tax is not applicable in {selectedState}</p>
                            )}
                          </CardContent>
                        </Card>
                      )}

                      {/* LWF */}
                      {stateLaws.lwf && (
                        <Card className={`${stateLaws.lwf.applicable ? 'bg-orange-50/50 border-orange-200' : 'bg-slate-50 border-slate-200'}`}>
                          <CardHeader className="pb-2">
                            <CardTitle className={`text-base flex items-center gap-2 ${stateLaws.lwf.applicable ? 'text-orange-800' : 'text-slate-500'}`}>
                              <Users size={16} />
                              {stateLaws.lwf.name || 'Labour Welfare Fund'}
                              {!stateLaws.lwf.applicable && <span className="text-xs bg-slate-200 px-2 py-0.5 rounded">Not Applicable</span>}
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="text-sm">
                            {stateLaws.lwf.applicable ? (
                              <div className="space-y-2">
                                <p className="text-slate-600">{stateLaws.lwf.description || 'Welfare fund for workers'}</p>
                                <div className="grid grid-cols-2 gap-2">
                                  <div className="bg-white p-2 rounded">
                                    <p className="text-xs text-slate-500">Employee</p>
                                    <p className="font-medium">{stateLaws.lwf.employee_contribution || 'N/A'}</p>
                                  </div>
                                  <div className="bg-white p-2 rounded">
                                    <p className="text-xs text-slate-500">Employer</p>
                                    <p className="font-medium">{stateLaws.lwf.employer_contribution || 'N/A'}</p>
                                  </div>
                                </div>
                                <p className="text-xs text-slate-500">Frequency: {stateLaws.lwf.frequency || 'Yearly'}</p>
                              </div>
                            ) : (
                              <p className="text-slate-500">Labour Welfare Fund is not applicable in {selectedState}</p>
                            )}
                          </CardContent>
                        </Card>
                      )}

                      {/* TDS */}
                      {stateLaws.tds && (
                        <Card className="bg-purple-50/50 border-purple-200">
                          <CardHeader className="pb-2">
                            <CardTitle className="text-base text-purple-800 flex items-center gap-2">
                              <IndianRupee size={16} />
                              {stateLaws.tds.name || 'Tax Deducted at Source (TDS)'}
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="text-sm space-y-2">
                            <p className="text-slate-600">{stateLaws.tds.description || 'Income tax deducted at source'}</p>
                            <div className="bg-white p-3 rounded">
                              <p className="text-xs text-slate-500 mb-2">Income Tax Slabs (New Regime FY 2024-25):</p>
                              <ul className="space-y-1">
                                {(stateLaws.tds.slabs || []).map((slab, i) => (
                                  <li key={i} className="text-sm">{slab}</li>
                                ))}
                              </ul>
                            </div>
                            <p className="text-xs text-slate-500">Due: {stateLaws.tds.due_date || '7th of following month'}</p>
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-slate-500">
                      <Scale size={48} className="mx-auto mb-4 text-slate-300" />
                      <p>Select a state from the list to view applicable laws</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Monthly Summary Tab */}
          <TabsContent value="summary">
            <Card>
              <CardHeader>
                <CardTitle>Monthly Compliance Summary - {selectedMonth} {selectedYear}</CardTitle>
                <CardDescription>Comprehensive statutory deductions breakdown</CardDescription>
              </CardHeader>
              <CardContent>
                {monthlySummary ? (
                  <div className="space-y-6">
                    {/* Status Banner */}
                    <div className="bg-slate-50 rounded-lg p-4 flex items-center justify-between flex-wrap gap-4">
                      <div>
                        <p className="text-sm text-slate-600">Total Working Employees</p>
                        <p className="text-2xl font-bold text-slate-900">{monthlySummary.total_employees || 0}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-slate-600">Payroll Processed</p>
                        <p className="text-2xl font-bold text-emerald-600">{monthlySummary.payroll_processed || 0}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm text-slate-600">Payroll Pending</p>
                        <p className="text-2xl font-bold text-amber-600">{monthlySummary.payroll_pending || 0}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-slate-600">Company State</p>
                        <p className="text-lg font-semibold text-indigo-600">{monthlySummary.company_state}</p>
                      </div>
                    </div>

                    {/* Estimated Section */}
                    {monthlySummary.estimated && (
                      <>
                        <div className="border-b pb-2">
                          <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                            <TrendingUp size={20} className="text-indigo-600" />
                            Estimated (Based on All {monthlySummary.total_employees || 0} Employees)
                          </h3>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <Card className="border-blue-200 bg-blue-50/50">
                            <CardHeader className="pb-2">
                              <CardTitle className="text-lg text-blue-800">PF (Est.)</CardTitle>
                            </CardHeader>
                            <CardContent>
                              <p className="text-2xl font-bold text-blue-800">₹{monthlySummary.estimated.pf.total?.toLocaleString()}</p>
                              <p className="text-xs text-slate-500">{monthlySummary.estimated.pf.employees} employees</p>
                            </CardContent>
                          </Card>

                          <Card className="border-emerald-200 bg-emerald-50/50">
                            <CardHeader className="pb-2">
                              <CardTitle className="text-lg text-emerald-800">ESI (Est.)</CardTitle>
                            </CardHeader>
                            <CardContent>
                              <p className="text-2xl font-bold text-emerald-800">₹{monthlySummary.estimated.esi.total?.toLocaleString()}</p>
                              <p className="text-xs text-slate-500">{monthlySummary.estimated.esi.employees} employees</p>
                            </CardContent>
                          </Card>

                          <Card className="border-amber-200 bg-amber-50/50">
                            <CardHeader className="pb-2">
                              <CardTitle className="text-lg text-amber-800">PT (Est.)</CardTitle>
                            </CardHeader>
                            <CardContent>
                              <p className="text-2xl font-bold text-amber-800">₹{monthlySummary.estimated.professional_tax.total?.toLocaleString()}</p>
                              <p className="text-xs text-slate-500">{monthlySummary.estimated.professional_tax.employees} employees</p>
                            </CardContent>
                          </Card>
                        </div>

                        <Card className="bg-indigo-50 border-indigo-200">
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <span className="font-semibold text-indigo-800">Estimated Total Statutory Cost</span>
                              <span className="text-2xl font-bold text-indigo-800">₹{monthlySummary.estimated.grand_total.total_statutory_cost?.toLocaleString()}</span>
                            </div>
                          </CardContent>
                        </Card>
                      </>
                    )}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-8">Loading summary...</p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* PF ECR Report */}
              <Card>
                <CardHeader className="bg-blue-50 pb-3">
                  <CardTitle className="text-base text-blue-800 flex items-center gap-2">
                    <FileText size={18} />
                    PF ECR Report
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => handleDownloadReport('pf', 'excel')} disabled={downloading['pf_excel']}>
                      Excel
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleDownloadReport('pf', 'csv')} disabled={downloading['pf_csv']}>
                      CSV
                    </Button>
                    <Button size="sm" className="bg-blue-600" onClick={() => handleDownloadReport('pf', 'pdf')} disabled={downloading['pf_pdf']}>
                      PDF
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* ESI Report */}
              <Card>
                <CardHeader className="bg-emerald-50 pb-3">
                  <CardTitle className="text-base text-emerald-800 flex items-center gap-2">
                    <FileText size={18} />
                    ESI Report
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => handleDownloadReport('esi', 'excel')} disabled={downloading['esi_excel']}>
                      Excel
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleDownloadReport('esi', 'csv')} disabled={downloading['esi_csv']}>
                      CSV
                    </Button>
                    <Button size="sm" className="bg-emerald-600" onClick={() => handleDownloadReport('esi', 'pdf')} disabled={downloading['esi_pdf']}>
                      PDF
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* PT Report */}
              <Card>
                <CardHeader className="bg-amber-50 pb-3">
                  <CardTitle className="text-base text-amber-800 flex items-center gap-2">
                    <FileText size={18} />
                    Professional Tax
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => handleDownloadReport('pt', 'excel')} disabled={downloading['pt_excel']}>
                      Excel
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleDownloadReport('pt', 'csv')} disabled={downloading['pt_csv']}>
                      CSV
                    </Button>
                    <Button size="sm" className="bg-amber-600" onClick={() => handleDownloadReport('pt', 'pdf')} disabled={downloading['pt_pdf']}>
                      PDF
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* TDS Report */}
              <Card>
                <CardHeader className="bg-purple-50 pb-3">
                  <CardTitle className="text-base text-purple-800 flex items-center gap-2">
                    <FileText size={18} />
                    TDS (Q{selectedQuarter})
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => handleDownloadReport('tds', 'excel')} disabled={downloading['tds_excel']}>
                      Excel
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleDownloadReport('tds', 'csv')} disabled={downloading['tds_csv']}>
                      CSV
                    </Button>
                    <Button size="sm" className="bg-purple-600" onClick={() => handleDownloadReport('tds', 'pdf')} disabled={downloading['tds_pdf']}>
                      PDF
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* LWF Report */}
              <Card>
                <CardHeader className="bg-orange-50 pb-3">
                  <CardTitle className="text-base text-orange-800 flex items-center gap-2">
                    <FileText size={18} />
                    LWF Report
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => handleDownloadReport('lwf', 'excel')} disabled={downloading['lwf_excel']}>
                      Excel
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => handleDownloadReport('lwf', 'csv')} disabled={downloading['lwf_csv']}>
                      CSV
                    </Button>
                    <Button size="sm" className="bg-orange-600" onClick={() => handleDownloadReport('lwf', 'pdf')} disabled={downloading['lwf_pdf']}>
                      PDF
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* TDS Forms Tab */}
          <TabsContent value="tds-forms">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Form 16 Card */}
              <Card className="border-indigo-200">
                <CardHeader className="bg-gradient-to-r from-indigo-50 to-purple-50">
                  <CardTitle className="flex items-center gap-2 text-indigo-800">
                    <FileCheck size={20} />
                    Form 16 - TDS Certificate
                  </CardTitle>
                  <CardDescription>
                    Certificate of Tax Deducted at Source on Salary (Section 203)
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  <div className="bg-indigo-50/50 p-4 rounded-lg space-y-2">
                    <h4 className="font-medium text-indigo-900">What is Form 16?</h4>
                    <p className="text-sm text-slate-600">
                      Form 16 is a certificate issued by employers to employees certifying the TDS 
                      deducted and deposited with the government. It contains:
                    </p>
                    <ul className="text-sm text-slate-600 list-disc list-inside space-y-1">
                      <li>Part A: TDS deducted quarterly summary</li>
                      <li>Part B: Details of salary paid and tax computed</li>
                      <li>Chapter VI-A deductions (if old regime)</li>
                    </ul>
                    <p className="text-xs text-amber-600 mt-2">
                      <AlertCircle size={12} className="inline mr-1" />
                      Issue deadline: 15th June of the following year
                    </p>
                  </div>

                  <Dialog open={form16DialogOpen} onOpenChange={setForm16DialogOpen}>
                    <DialogTrigger asChild>
                      <Button className="w-full bg-indigo-600 hover:bg-indigo-700">
                        <Download className="mr-2" size={18} />
                        Generate Form 16
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                          <FileCheck className="text-indigo-600" size={20} />
                          Generate Form 16
                        </DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <Label>Select Employee *</Label>
                          <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                            <SelectTrigger>
                              <SelectValue placeholder="Select an employee" />
                            </SelectTrigger>
                            <SelectContent>
                              {employees.filter(e => e.employment_status !== 'Exit').map(emp => (
                                <SelectItem key={emp.id} value={emp.id}>
                                  <div className="flex items-center justify-between gap-4">
                                    <span>{emp.name || emp.employee_id}</span>
                                    <span className="text-xs text-slate-400">{emp.employee_id}</span>
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Financial Year *</Label>
                          <Select value={selectedFinancialYear} onValueChange={setSelectedFinancialYear}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {financialYears.map(fy => (
                                <SelectItem key={fy} value={fy}>FY {fy}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <p className="text-xs text-slate-500">
                          Form 16 will include salary details from April {selectedFinancialYear.split('-')[0]} 
                          to March {parseInt(selectedFinancialYear.split('-')[0]) + 1}
                        </p>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setForm16DialogOpen(false)}>Cancel</Button>
                        <Button 
                          onClick={handleDownloadForm16} 
                          disabled={!selectedEmployee || downloading.form16}
                          className="bg-indigo-600 hover:bg-indigo-700"
                        >
                          {downloading.form16 ? 'Generating...' : 'Download PDF'}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </CardContent>
              </Card>

              {/* Form 24Q Card */}
              <Card className="border-purple-200">
                <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50">
                  <CardTitle className="flex items-center gap-2 text-purple-800">
                    <FileSpreadsheet size={20} />
                    Form 24Q - Quarterly TDS Return
                  </CardTitle>
                  <CardDescription>
                    Quarterly Statement of TDS on Salary (Section 192)
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  <div className="bg-purple-50/50 p-4 rounded-lg space-y-2">
                    <h4 className="font-medium text-purple-900">What is Form 24Q?</h4>
                    <p className="text-sm text-slate-600">
                      Form 24Q is a quarterly statement filed by employers containing details of 
                      TDS deducted from employees' salaries. It includes:
                    </p>
                    <ul className="text-sm text-slate-600 list-disc list-inside space-y-1">
                      <li>Part A: Deductor details and summary</li>
                      <li>Part B: Deductee-wise details (Annexure II)</li>
                      <li>Challan details and verification</li>
                    </ul>
                    <div className="grid grid-cols-2 gap-2 mt-3">
                      <div className="text-xs p-2 bg-white rounded">
                        <p className="font-medium">Q1 (Apr-Jun)</p>
                        <p className="text-slate-500">Due: 31st July</p>
                      </div>
                      <div className="text-xs p-2 bg-white rounded">
                        <p className="font-medium">Q2 (Jul-Sep)</p>
                        <p className="text-slate-500">Due: 31st Oct</p>
                      </div>
                      <div className="text-xs p-2 bg-white rounded">
                        <p className="font-medium">Q3 (Oct-Dec)</p>
                        <p className="text-slate-500">Due: 31st Jan</p>
                      </div>
                      <div className="text-xs p-2 bg-white rounded">
                        <p className="font-medium">Q4 (Jan-Mar)</p>
                        <p className="text-slate-500">Due: 31st May</p>
                      </div>
                    </div>
                  </div>

                  <Dialog open={form24QDialogOpen} onOpenChange={setForm24QDialogOpen}>
                    <DialogTrigger asChild>
                      <Button className="w-full bg-purple-600 hover:bg-purple-700">
                        <Download className="mr-2" size={18} />
                        Generate Form 24Q
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                          <FileSpreadsheet className="text-purple-600" size={20} />
                          Generate Form 24Q
                        </DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <Label>Select Quarter *</Label>
                          <Select value={form24QQuarter.toString()} onValueChange={(v) => setForm24QQuarter(parseInt(v))}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="1">Q1 (April - June)</SelectItem>
                              <SelectItem value="2">Q2 (July - September)</SelectItem>
                              <SelectItem value="3">Q3 (October - December)</SelectItem>
                              <SelectItem value="4">Q4 (January - March)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label>Financial Year *</Label>
                          <Select value={selectedFinancialYear} onValueChange={setSelectedFinancialYear}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {financialYears.map(fy => (
                                <SelectItem key={fy} value={fy}>FY {fy}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="p-3 bg-purple-50 rounded-lg">
                          <p className="text-sm text-purple-800">
                            <strong>Selected Period:</strong> Q{form24QQuarter} of FY {selectedFinancialYear}
                          </p>
                          <p className="text-xs text-slate-500 mt-1">
                            {form24QQuarter === 1 && `April - June ${selectedFinancialYear.split('-')[0]}`}
                            {form24QQuarter === 2 && `July - September ${selectedFinancialYear.split('-')[0]}`}
                            {form24QQuarter === 3 && `October - December ${selectedFinancialYear.split('-')[0]}`}
                            {form24QQuarter === 4 && `January - March ${parseInt(selectedFinancialYear.split('-')[0]) + 1}`}
                          </p>
                        </div>
                      </div>
                      <DialogFooter>
                        <Button variant="outline" onClick={() => setForm24QDialogOpen(false)}>Cancel</Button>
                        <Button 
                          onClick={handleDownloadForm24Q} 
                          disabled={downloading.form24q}
                          className="bg-purple-600 hover:bg-purple-700"
                        >
                          {downloading.form24q ? 'Generating...' : 'Download PDF'}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </CardContent>
              </Card>
            </div>

            {/* Quick Info Section */}
            <Card className="mt-6 border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg">TDS Compliance Quick Reference</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="p-4 bg-blue-50 rounded-lg">
                    <h4 className="font-medium text-blue-800 mb-2">TDS Payment</h4>
                    <p className="text-sm text-slate-600">
                      Due by 7th of the following month (15th for March)
                    </p>
                  </div>
                  <div className="p-4 bg-green-50 rounded-lg">
                    <h4 className="font-medium text-green-800 mb-2">Form 16 Issuance</h4>
                    <p className="text-sm text-slate-600">
                      Must be issued by 15th June after the financial year
                    </p>
                  </div>
                  <div className="p-4 bg-amber-50 rounded-lg">
                    <h4 className="font-medium text-amber-800 mb-2">Late Filing Penalty</h4>
                    <p className="text-sm text-slate-600">
                      ₹200/day for each day of delay (max: TDS amount)
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Rates Tab */}
          <TabsContent value="rates">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-blue-800">PF Rates</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between p-2 bg-slate-50 rounded">
                      <span>Employee Contribution</span>
                      <span className="font-bold">{dashboard?.pf_rates?.employee_rate}</span>
                    </div>
                    <div className="flex justify-between p-2 bg-slate-50 rounded">
                      <span>Employer Contribution</span>
                      <span className="font-bold">{dashboard?.pf_rates?.employer_rate}</span>
                    </div>
                    <div className="flex justify-between p-2 bg-slate-50 rounded">
                      <span>Wage Ceiling</span>
                      <span className="font-bold">₹{dashboard?.pf_rates?.wage_ceiling?.toLocaleString()}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-emerald-800">ESI Rates</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between p-2 bg-slate-50 rounded">
                      <span>Employee Contribution</span>
                      <span className="font-bold">{dashboard?.esi_rates?.employee_rate}</span>
                    </div>
                    <div className="flex justify-between p-2 bg-slate-50 rounded">
                      <span>Employer Contribution</span>
                      <span className="font-bold">{dashboard?.esi_rates?.employer_rate}</span>
                    </div>
                    <div className="flex justify-between p-2 bg-slate-50 rounded">
                      <span>Wage Ceiling</span>
                      <span className="font-bold">₹{dashboard?.esi_rates?.wage_ceiling?.toLocaleString()}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default StatutoryCompliance;
